import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiService } from '../services/api';

/**
 * Custom hook for fetching and managing event meetings from the iConnections SQL Server.
 * Used to show meeting badges on philanthropist/nonprofit cards.
 * 
 * @param {number} eventId - The F4C Event ID
 * @param {number} icEventId - The iConnections Event ID
 * @param {object} options - Optional configuration
 * @param {boolean} options.autoFetch - Whether to automatically fetch on mount (default: true)
 * 
 * @returns {object} - Meetings state and helper functions
 */
const useEventMeetings = (eventId, icEventId, options = {}) => {
    const { autoFetch = true } = options;

    const [meetings, setMeetings] = useState([]);
    const [userEventCompanyId, setUserEventCompanyId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Fetch meetings for the event
     */
    const fetchMeetings = useCallback(async () => {
        if (!eventId || !icEventId) {
            console.warn('useEventMeetings: Missing eventId or icEventId');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            console.log('[useEventMeetings] Fetching meetings for event:', eventId, 'ic_event:', icEventId);
            const response = await apiService.getEventMeetings(eventId, icEventId);

            if (response.data.success) {
                setMeetings(response.data.meetings || []);
                setUserEventCompanyId(response.data.user_event_company_id);
                setLoaded(true);
                
                console.log('[useEventMeetings] Meetings loaded:', {
                    count: response.data.meetings?.length || 0,
                    userEventCompanyId: response.data.user_event_company_id
                });
            } else {
                setError(response.data.error || 'Failed to load meetings');
                setLoaded(true);
                console.error('[useEventMeetings] Failed to load meetings:', response.data.error);
            }
        } catch (err) {
            console.error('[useEventMeetings] Error fetching meetings:', err);
            setError('Failed to fetch meetings from server');
            setLoaded(true);
        } finally {
            setLoading(false);
        }
    }, [eventId, icEventId]);

    /**
     * Get meeting status for a company by their iConnections EventCompanyId
     * 
     * @param {number|string} icEventCompanyId - The iConnections EventCompanyId to check
     * @returns {object|null} - { status, meeting, isOrganizer } or null if no meeting
     * 
     * Status values:
     * - 'confirmed' (statusId = 2): Meeting is confirmed
     * - 'pending_sent' (statusId = 1, user is organizer): User sent request, waiting for response
     * - 'pending_received' (statusId = 1, user is receiver): User received request, needs to respond
     * - 'cancelled' (statusId = 3): Meeting was cancelled
     */
    const getMeetingStatusForCompany = useCallback((icEventCompanyId) => {
        if (!icEventCompanyId || !userEventCompanyId) return null;

        const companyId = parseInt(icEventCompanyId, 10);

        // Find meeting where this company is either organizer or receiver
        const meeting = meetings.find(m => 
            m.organizer_EventCompanyId === companyId || 
            m.receiver_EventCompanyId === companyId
        );

        if (!meeting) return null;

        // Determine if current user is the organizer
        const isOrganizer = meeting.organizer_EventCompanyId === userEventCompanyId;
        const isReceiver = meeting.receiver_EventCompanyId === userEventCompanyId;

        // Determine status
        let status;
        if (meeting.meetingStatusId === 2) {
            status = 'confirmed';
        } else if (meeting.meetingStatusId === 3) {
            status = 'cancelled';
        } else if (meeting.meetingStatusId === 1) {
            // Pending - determine if sent or received based on user's role
            status = isOrganizer ? 'pending_sent' : 'pending_received';
        } else {
            status = 'unknown';
        }

        // Determine if it's post-optimization
        const isPostOptimization = 
            (meeting.hasOptimization === true && meeting.isOptimized === true) || 
            meeting.hasOptimization === false;

        return {
            status,
            meeting,
            isOrganizer,
            isReceiver,
            meetingStatusId: meeting.meetingStatusId,
            isPostOptimization,
            hasOptimization: meeting.hasOptimization,
            isOptimized: meeting.isOptimized
        };
    }, [meetings, userEventCompanyId]);

    /**
     * Check if a company has any meeting with the current user
     */
    const hasMeetingWithCompany = useCallback((icEventCompanyId) => {
        return getMeetingStatusForCompany(icEventCompanyId) !== null;
    }, [getMeetingStatusForCompany]);

    /**
     * Get all meetings where user is organizer
     */
    const sentMeetings = useMemo(() => 
        meetings.filter(m => m.organizer_EventCompanyId === userEventCompanyId),
        [meetings, userEventCompanyId]
    );

    /**
     * Get all meetings where user is receiver
     */
    const receivedMeetings = useMemo(() => 
        meetings.filter(m => m.receiver_EventCompanyId === userEventCompanyId),
        [meetings, userEventCompanyId]
    );

    /**
     * Get confirmed meetings
     */
    const confirmedMeetings = useMemo(() => 
        meetings.filter(m => m.meetingStatusId === 2),
        [meetings]
    );

    /**
     * Get pending meetings where action is required (user is receiver)
     */
    const pendingActionMeetings = useMemo(() => 
        meetings.filter(m => 
            m.meetingStatusId === 1 && 
            m.receiver_EventCompanyId === userEventCompanyId
        ),
        [meetings, userEventCompanyId]
    );

    /**
     * Refresh meetings data
     */
    const refresh = useCallback(() => {
        return fetchMeetings();
    }, [fetchMeetings]);

    /**
     * Clear all meetings data
     */
    const clear = useCallback(() => {
        setMeetings([]);
        setUserEventCompanyId(null);
        setLoading(false);
        setLoaded(false);
        setError(null);
    }, []);

    // Auto-fetch on mount if enabled and we have required IDs
    useEffect(() => {
        if (autoFetch && eventId && icEventId) {
            fetchMeetings();
        }
    }, [autoFetch, eventId, icEventId, fetchMeetings]);

    return {
        // State
        meetings,
        userEventCompanyId,
        loading,
        loaded,
        error,

        // Computed
        sentMeetings,
        receivedMeetings,
        confirmedMeetings,
        pendingActionMeetings,
        totalMeetings: meetings.length,
        pendingActionCount: pendingActionMeetings.length,

        // Actions
        fetchMeetings,
        refresh,
        clear,

        // Utilities
        getMeetingStatusForCompany,
        hasMeetingWithCompany
    };
};

export default useEventMeetings;
